import { DeesToast } from './00group-feedback/dees-toast/dees-toast.js';

/** Payload of the cancelable `dees-action-error` event. */
export interface IActionErrorDetail<TAction = unknown> {
  /** Whatever the action callback rejected with. */
  error: unknown;
  /** The action descriptor whose callback rejected. */
  action: TAction;
  /**
   * Where the action was invoked from. Catalog sources are `modal-menu`,
   * `table-header`, `table-footer`, `table-row`, `table-rowmenu`,
   * `table-contextmenu` and `table-doubleclick`.
   */
  source: string;
}

/** The event a component dispatches before it falls back to a toast. */
export const actionErrorEventName = 'dees-action-error';

/** Human-readable text for an unknown rejection value. */
export const actionErrorMessage = (errorArg: unknown): string => {
  if (errorArg instanceof Error) return errorArg.message || errorArg.name;
  if (typeof errorArg === 'string' && errorArg) return errorArg;
  const text = String(errorArg);
  return text === '[object Object]' || text === 'undefined' || text === 'null' ? 'The action failed.' : text;
};

/**
 * Announces a failed action on `hostArg` and, unless a listener cancels the
 * event, surfaces it through the catalog's own notification primitive. The
 * event is cancelable so an application can render its own error surface.
 */
export const reportActionError = <TAction>(
  hostArg: HTMLElement,
  detailArg: IActionErrorDetail<TAction>,
  ownerElementArg?: Node | null,
): void => {
  const handled = !hostArg.dispatchEvent(new CustomEvent<IActionErrorDetail<TAction>>(actionErrorEventName, {
    detail: detailArg,
    bubbles: true,
    composed: true,
    cancelable: true,
  }));
  if (handled) return;
  void DeesToast.show({
    message: actionErrorMessage(detailArg.error),
    type: 'error',
    ownerElement: ownerElementArg ?? undefined,
  });
};

/**
 * Awaits an action callback so a rejected promise becomes a visible failure
 * instead of an unhandled rejection. Successful actions resolve unchanged.
 */
export const runAction = async <TAction>(
  hostArg: HTMLElement,
  detailArg: { action: TAction; source: string; ownerElement?: Node | null },
  runArg: () => unknown,
): Promise<void> => {
  try {
    await runArg();
  } catch (error) {
    reportActionError(hostArg, { error, action: detailArg.action, source: detailArg.source }, detailArg.ownerElement);
  }
};
